/**
 * Service to handle dynamic SEO and tracking tag injection
 * after bootstrap data is loaded
 */

export type SeoData = {
  customerName: string
  customerImgUrl: string
  sentryTracking: boolean
  pingdomTracking: boolean
}

class SeoService {
  /**
   * Update page title and meta tags based on customer data
   */
  updateSeoTags(data: SeoData): void {
    const { customerName, customerImgUrl } = data

    // Update page title
    if (customerName) {
      document.title = customerName
      this.updateMetaTag("og:title", `${customerName}'s Network on Wavelength`)
    }

    // Update OG image
    if (customerImgUrl) {
      this.updateMetaTag("og:image", customerImgUrl)
    }

    // Update OG description (static but good to have consistency)
    this.updateMetaTag(
      "og:description",
      "Wavelength extends your network so you can find jobs, connect with friends, and keep your organization updated. Forget the likes, comments, and connections; it's just search.",
    )
  }

  /**
   * Inject tracking scripts based on customer settings
   */
  injectTrackingScripts(data: SeoData): void {
    const { sentryTracking, pingdomTracking } = data

    // Inject Sentry script if needed
    if (sentryTracking && !window.sentryLoaded) {
      this.injectSentryScript()
      window.sentryLoaded = true
    }

    // Inject Pingdom script if needed
    if (pingdomTracking && !window.pingdomLoaded) {
      this.injectPingdomScript()
      window.pingdomLoaded = true
    }
  }

  private updateMetaTag(property: string, content: string): void {
    // Try to find existing meta tag
    let metaTag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement

    if (!metaTag) {
      // Create new meta tag if it doesn't exist
      metaTag = document.createElement("meta")
      metaTag.setAttribute("property", property)
      document.head.appendChild(metaTag)
    }

    metaTag.setAttribute("content", content)
  }

  private injectSentryScript(): void {
    const script = document.createElement("script")
    script.src = "https://cdn.ravenjs.com/3.24.0/raven.min.js"
    script.crossOrigin = "anonymous"
    script.onload = () => {
      // Configure Sentry after script loads
      const configScript = document.createElement("script")
      configScript.textContent = `
        window.errorsOn = true;
        Raven.config('https://54e1f0f218d94e0b92e28672df956673@sentry.io/1244953', {
          release: '0-0-0',
          environment: 'production',
        }).install();
      `
      document.head.appendChild(configScript)
    }
    document.head.appendChild(script)
  }

  private injectPingdomScript(): void {
    const script = document.createElement("script")
    script.textContent = `
      var _prum = [['id', '5930522b7ca8f460e837f483'],
      ['mark', 'firstbyte', (new Date()).getTime()]];
      (function () {
        var s = document.getElementsByTagName('script')[0]
          , p = document.createElement('script');
        p.async = 'async';
        p.src = '//rum-static.pingdom.net/prum.min.js';
        s.parentNode.insertBefore(p, s);
      })();
    `
    document.head.appendChild(script)
  }
}

// Create singleton instance
export const seoService = new SeoService()

// Type declarations for global tracking flags
declare global {
  interface Window {
    sentryLoaded?: boolean
    pingdomLoaded?: boolean
  }
}
